import { Injectable, OnDestroy } from '@angular/core';
import { cloneDeep } from '@apollo/client/utilities';
import documentFundQuery from '@graphql/fund-document/fund-documents.graphql';
import { LiteratureListingBaseService } from '@literature/literature-document-listing/services/literature-listing-base.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { Product } from '@models';
import { FundOverviewService } from '@products/services/fund-overview.service';
import { SegmentService } from '@services/segment.service';
import {
  DocumentMatchCondition,
  LiteratureData,
  LiteratureDocument,
  LiteratureDocumentListingData,
  ProductDetailConfiguration,
  TabKey,
} from '@types';
import { Logger } from '@utils/logger';
import { forkJoin } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { ProfileService } from '@services/profile.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { HeaderDocumentsService } from '@products/fund-header/services/header-documents.service';
import {
  MONTHLY_COMMENTARY,
  QUARTERLY_COMMENTARY,
  FUND_COMMENTARY,
} from '@utils/app.constants';
import { TranslateService } from '@shared/translate/translate.service';

const logger = Logger.getLogger('LiteratureFundDocumentService');

@Injectable({
  providedIn: 'root',
})
export class LiteratureFundDocumentService
  extends LiteratureListingBaseService
  implements OnDestroy {
  constructor(
    protected literatureDataService: LiteratureDataService,
    private overviewService: FundOverviewService,
    private headerDocumentsService: HeaderDocumentsService,
    protected segmentService: SegmentService,
    protected cartService: CartHandlerService,
    protected profileService: ProfileService,
    protected literatureConfigService: LiteratureGlobalConfigService,
    protected translateService: TranslateService
  ) {
    super(
      literatureDataService,
      literatureConfigService,
      cartService,
      segmentService,
      profileService,
      translateService
    );
  }
  documentTypes = new Map();
  dateFormat;
  paramMap: Map<string, string>;

  public populate(productDetailConfiguration: ProductDetailConfiguration) {
    if (this.isInternational) {
      this.overviewService
        .register(documentFundQuery, {
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        })
        .pipe(
          switchMap((data: Product) => {
            this.productData(data);
            this.paramMap = new Map();
            this.paramMap
              .set('cache', 'true')
              .set('frkOneTISNumber', productDetailConfiguration.fundId)
              .set('shareClass', productDetailConfiguration.shareClassCode)
              .set('fundUmbrella', data.fundUmbrella);
            // Audience new flow
            if (this.literatureConfigService.checkAudienceFromOverrideList()) {
              this.paramMap.set(
                'audience',
                this.literatureConfigService.literatureConfig?.fundDocumentSegmentOverrideList?.toString()
              );
            }
            return this.literatureDataService
              .loadData(this.paramMap, true)
              .pipe(
                map((litData) => {
                  const combinedData: [Product, LiteratureData] = [
                    data,
                    litData,
                  ];

                  const documentsWithPrewiew: string[] =
                    this.literatureConfigService?.literatureConfig
                      .documentsWithPreview || [];
                  this.headerDocumentsService.updatePreviewConfig(
                    documentsWithPrewiew
                  );
                  this.mergeData(combinedData);
                })
              );
          })
        )
        .subscribe();
    } else {
      this.literatureDataService
        .getProfileData$()
        ?.pipe(
          takeUntil(this.unsubscribe$),
          switchMap((data) => {
            this.literatureDataService.setProfileData(data);
            const productData$ = this.overviewService.register(
              documentFundQuery,
              {
                fundId: productDetailConfiguration.fundId,
                shareClassCode: productDetailConfiguration.shareClassCode,
              }
            );
            this.paramMap = new Map();
            this.paramMap
              .set('cache', 'true')
              .set('frkOneTISNumber', productDetailConfiguration.fundId)
              .set('shareClass', productDetailConfiguration.shareClassCode);
            // Audience new flow
            if (this.literatureConfigService.checkAudienceFromOverrideList()) {
              this.paramMap.set(
                'audience',
                this.literatureConfigService.literatureConfig?.fundDocumentSegmentOverrideList?.toString()
              );
            }
            const litData$ = this.literatureDataService.loadData(
              this.paramMap,
              true
            );
            return forkJoin([productData$, litData$]);
          })
        )
        .subscribe(
          (data) => {
            const [productData, litData] = data;
            // TODO - this code doesn't look correct. We should use foreach, not map for tabs
            this.productData(productData);

            const documentsWithPrewiew: string[] =
              this.literatureConfigService?.literatureConfig
                .documentsWithPreview || [];
            this.headerDocumentsService.updatePreviewConfig(
              documentsWithPrewiew
            );

            this.mergeData(data);
          },
          (err) => {
            if (err?.status === 404) {
              logger.warn(
                '[populate] ' +
                  err?.messaage +
                  ' code: ' +
                  err?.error?.error?.errorList[0]?.code
              );
            }
          }
        );
    }
  }

  getColumnToDisplay(): string[] {
    return this.literatureConfigService.literatureConfig
      ?.fundDocumentGridColumn;
  }

  // reads array of documents to be displayed with 'lock'/require login
  getLoggedInColumnToDisplay(): string[] {
    return this.literatureConfigService?.literatureConfig
      ?.documentTypeDisplayForLoggedIn;
  }

  /**
   * helper method to not duplicate code
   * @param key object defining tab label and document type
   */
  private mapKeyToTabs(key: TabKey): void {
    // This is dependency for fundheader component
    // it prevens 'HEADER-DOCUMENTS' tab from being displayed and sends data to headerService
    if (key.documentTab !== 'HEADER-DOCUMENTS') {
      this.documentTypes.set(key.documentTab, key.documentTypeDisplay);
    }

    if (key.documentTab === 'HEADER-DOCUMENTS') {
      this.headerDocumentsService.updateConfig(key.documentTypeDisplay);
    }
  }
  /**
   * mergeData from literature and product data services
   * @param data data received from literature service
   */
  private mergeData(data): void {
    logger.debug('merge data for literature fund document : ', data);
    this.loggedInColumnToDisplay = this.getLoggedInColumnToDisplay();
    const listingData: LiteratureDocumentListingData[] = [];
    const [fundListing, litData]: [Product, LiteratureData] = data;
    this.dateFormat = this.literatureConfigService?.dateFormat;
    const productData = fundListing;
    // Audience new flow logic to check map
    const checkDocsWithoutAudience = true;

    const fromOverideList = this.literatureConfigService.checkAudienceFromOverrideList();

    for (const literatureData of litData.document) {
      data = this.literatureDataService.literatureMapper(literatureData);

      // WDE-441 flag usePreviewLink informs components to redirect user to preview page instead download.
      data.usePreviewLink = this.literatureConfigService?.literatureConfig?.documentsWithPreview?.includes(
        data.dctermsType
      );
      // markOld:  Audience existing logic to check map
      const checkAudienceForDocs = this.checkAudience(
        data,
        this.literatureConfigService.literatureConfig
          .fundDocumentSegmentOverrideList
      );
      // switch logic based on flags at channel level
      const checkMapping = fromOverideList
        ? checkDocsWithoutAudience
        : checkAudienceForDocs;
      const matchOn = this.literatureConfigService
        .getDocumentMappingConfiguration()
        .get(data.dctermsType);
      if (checkMapping) {
        this.loadShareClassData(
          cloneDeep(data),
          productData,
          listingData,
          !(matchOn === DocumentMatchCondition.FUND_ID)
        );
      }
    }
    // send documents to header-documents service to use in other components.
    this.headerDocumentsService.updateDocuments(listingData);
    this.dataDisplayByTab(listingData);
  }

  private loadShareClassData(
    litData: LiteratureDocument,
    ppssData: any,
    listingData: LiteratureDocumentListingData[],
    showShareClass: boolean
  ): void {
    this.checkShareClassAvailablity(litData, ppssData, showShareClass);
    if (this.isValidDocument(litData)) {
      this.loadDocumentList(litData, ppssData, listingData);
    }
  }

  // to pass other document type and commentary document with recent literature document
  private isValidDocument(litData: LiteratureDocument): boolean {
    return !(
      this.checkCommentaryDocument(litData?.dctermsType) &&
      litData.isHistoryData
    );
  }

  private checkCommentaryDocument(dctermType: string): boolean {
    const commentaryDocTypes = [
      MONTHLY_COMMENTARY,
      QUARTERLY_COMMENTARY,
      FUND_COMMENTARY,
    ];
    return commentaryDocTypes.includes(dctermType);
  }

  /**
   * splits data into tabs/columns
   * @param data LiteratureDocumentListingData array
   */
  private dataDisplayByTab(data: LiteratureDocumentListingData[]) {
    const dataTab = new Map();
    for (const [tab, docTypes] of this.documentTypes.entries()) {
      data.forEach((element) => {
        if (docTypes?.includes(element.literatureData.dctermsType?.trim())) {
          if (dataTab.has(tab)) {
            const documents = dataTab.get(tab);
            documents.push(element);
            dataTab.set(tab, documents);
          } else {
            dataTab.set(tab, [element]);
          }
        }
      });
    }
    this.state.datatab = this.sortDataByConfig(dataTab);
    this.documentListingData$.next(this.state);
  }

  /**
   * Below method will sort the document data based on literature config
   */
  sortDataByConfig(dataTab) {
    let counter = 0;
    dataTab.forEach((tabData: LiteratureDocumentListingData[]) => {
      const sortOrder = this.documentTypes.get(
        Array.from(this.documentTypes.keys())[counter]
      );
      // Sort documents based on sequence available in literature config
      tabData.sort(
        (
          a: LiteratureDocumentListingData,
          b: LiteratureDocumentListingData
        ) => {
          const A: string = a.literatureData?.dctermsType;
          const B: string = b.literatureData?.dctermsType;
          if (sortOrder.indexOf(A) > sortOrder.indexOf(B)) {
            return 1;
          } else {
            return -1;
          }
        }
      );
      counter++;

      const documentGroupSortResults = tabData.reduce(
        (results: any, document: LiteratureDocumentListingData) => {
          results[document?.literatureData?.dctermsType] = [
            ...(results[document?.literatureData?.dctermsType] || []),
            document,
          ];
          // Sort each document type group alphabetically
          results[document?.literatureData?.dctermsType]?.sort(
            (
              a: LiteratureDocumentListingData,
              b: LiteratureDocumentListingData
            ) => {
              return a.literatureData?.dctermsTitle
                ?.toLocaleLowerCase()
                .localeCompare(
                  b.literatureData?.dctermsTitle?.toLocaleLowerCase()
                );
            }
          );
          return results;
        },
        Object.create(null)
      );

      const finalSortedDocuments: LiteratureDocumentListingData[] = Object.keys(
        documentGroupSortResults
      ).reduce((groupValues: any[], groupName: string) => {
        return groupValues.concat(documentGroupSortResults[groupName]);
      }, []);

      tabData.splice(0, tabData.length, ...finalSortedDocuments);
    });
    return dataTab;
  }

  productData(data: Product): void {
    this.literatureConfigService?.literatureConfig[
      data.productType
    ]?.tabs.forEach((key: TabKey) => {
      this.mapKeyToTabs(key);
    });
  }

  ngOnDestroy(): void {
    this.onDestroyUnsubscribe();
  }
}
